import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import {
	VipActiveMemberActiveIcon,
	VipActiveMemberBanner,
	VipActiveMemberCalendar,
	VipActiveMemberCardBack,
	VipActiveMemberLoyaltyBadge,
	VipActiveMemberLoyaltyReward,
	VipActiveMemberProfileFrame,
	VipActiveMemberSubscriptionSettings,
	VipActiveMemberWeeklyGift,
} from '../../assets';
import { vipCardGlow } from '../../shared/glowStyles';
import { useSafeViewport } from '../../utils/hooks/useSafeViewport';
import { useSvgId } from '../../utils/hooks/useSvgId';
import { getNavPlateWidth } from '../Components/AppFooter/Nav/FooterNavButtonsV2';
import GoldGradientText from '../Components/common/GoldGradientText';
import { IndicatorScrollView } from '../Components/common/ScrollIndicator';
import { getDesktopColumns } from '../Components/Desktop/DesktopBottomBars';
import { desktopScrollFade, useDesktopLayout } from '../Components/Desktop/desktopLayout';
import { MainLayout } from '../Pages/MainLayout';

const BASE_WIDTH = 390;
const DESIGN_CONTENT_WIDTH = 360;
const NAV_ART_WIDTH = 371;
const NAV_ROW_WIDTH = 361;
const FONT_FAMILY = 'Sofia Sans';
const FONT_FAMILY_DISPLAY = 'Sofia Sans Semi Condensed';

const PANEL_BACKGROUND = {
	card: '#571405',
	section: '#330b05',
	highlight: '#770700',
} as const;

type EventPassProps = {
	title: string;
	time: string;
	scale: number;
};

const getScale = (width: number) => Math.min(width, BASE_WIDTH) / BASE_WIDTH;

const VipPanel = ({
	children,
	style,
	variant = 'card',
	scale,
}: React.PropsWithChildren<{
	style?: any;
	variant?: 'card' | 'section' | 'highlight';
	scale: number;
}>) => (
	<View
		style={[
			styles.panel,
			{ backgroundColor: PANEL_BACKGROUND[variant], borderRadius: 10 * scale },
			style,
		]}
	>
		{children}
	</View>
);

const SectionHeading = ({
	title,
	accessory,
	scale,
}: {
	title: string;
	accessory?: string;
	scale: number;
}) => (
	<View style={styles.sectionHeading}>
		<Text style={[styles.title, { fontSize: 16 * scale, lineHeight: 19 * scale }]}>{title}</Text>
		{accessory ? (
			<Text style={[styles.headingAccessory, { fontSize: 12 * scale }]}>{accessory}</Text>
		) : null}
	</View>
);

const SurfaceField = ({
	width,
	height,
	radius,
}: {
	width: number;
	height: number;
	radius: number;
}) => {
	const gradientId = useSvgId('vipEventPassSurface');

	return (
		<Svg
			width={width}
			height={height}
			viewBox={`0 0 ${width} ${height}`}
			style={styles.surfaceField}
			pointerEvents='none'
		>
			<Defs>
				<LinearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
					<Stop offset='0' stopColor='#770700' />
					<Stop offset='0.80127' stopColor='#320400' />
					<Stop offset='1' stopColor='#320400' />
				</LinearGradient>
			</Defs>
			<Rect width={width} height={height} rx={radius} fill={`url(#${gradientId})`} />
		</Svg>
	);
};

const EventPass = ({ title, time, scale }: EventPassProps) => {
	const { t } = useTranslation();

	return (
		<VipPanel
			scale={scale}
			variant='highlight'
			style={[styles.eventPass, { minHeight: 66 * scale, padding: 8 * scale, gap: 8 * scale }]}
		>
			<SurfaceField width={336 * scale} height={66 * scale} radius={9 * scale} />
			<View
				style={[
					styles.eventThumb,
					{ width: 50 * scale, height: 50 * scale, borderRadius: 5 * scale },
				]}
			/>
			<View style={styles.eventCopy}>
				<GoldGradientText
					text={title}
					width={140 * scale}
					height={22 * scale}
					fontSize={16 * scale}
					fontFamily={FONT_FAMILY_DISPLAY}
					fontWeight='600'
					align='left'
				/>
				<Text style={[styles.label, { fontSize: 12 * scale, lineHeight: 14 * scale }]}>
					{t('T_VIP_PREMIUM_PASS_ACTIVE')}
				</Text>
			</View>
			<View style={{ width: 94 * scale }}>
				<Text style={[styles.muted, { fontSize: 12 * scale, lineHeight: 14 * scale }]}>
					{t('T_VIP_ENDS_IN')}
				</Text>
				<GoldGradientText
					text={time}
					width={94 * scale}
					height={18 * scale}
					fontSize={12 * scale}
					fontWeight='600'
					align='left'
				/>
			</View>
		</VipPanel>
	);
};

const VipActiveMemberScreen = () => {
	const { t } = useTranslation();
	const { width, height, insets } = useSafeViewport();
	const navWidth = getNavPlateWidth(width, height, true);
	const contentWidth = navWidth
		? navWidth * (NAV_ROW_WIDTH / NAV_ART_WIDTH)
		: Math.min(width - 30, DESIGN_CONTENT_WIDTH);
	const scale = contentWidth > 0 ? contentWidth / DESIGN_CONTENT_WIDTH : getScale(width);
	const stylesByScale = getScaledStyles(scale, contentWidth, insets.bottom);
	const desktop = useDesktopLayout();

	// The six blocks in two groups - the membership itself, then what it includes - so the desktop
	// frame can put them side by side while mobile stacks them in one column as before.
	const membershipBlocks = (stylesByScale: ScaledStyles, scale: number) => (
		<>
			<VipPanel scale={scale} variant='highlight' style={stylesByScale.membershipHero}>
				<Image
					source={VipActiveMemberBanner}
					style={stylesByScale.membershipBanner}
					resizeMode='cover'
				/>
				<View style={stylesByScale.membershipStatus}>
					<Text style={[stylesByScale.title, stylesByScale.membershipTitle]}>
						{t('T_VIP_MEMBERSHIP_STATUS')}
					</Text>
					<View style={stylesByScale.activeRow}>
						<VipActiveMemberActiveIcon width={14 * scale} height={14 * scale} />
						<Text style={stylesByScale.activeText}> {t('T_VIP_STATUS_ACTIVE')}</Text>
					</View>
					<View style={stylesByScale.billingBlock}>
						<Text style={stylesByScale.billingLabel}>{t('T_VIP_NEXT_BILLING')}</Text>
						<Text style={stylesByScale.billingDate}>12 AUG 2025</Text>
					</View>
					<Text style={stylesByScale.renewalNotice}>{t('T_VIP_RENEWAL_NOTICE')}</Text>
				</View>
			</VipPanel>

			<VipPanel scale={scale} variant='highlight' style={stylesByScale.loyaltyCard}>
				<View style={stylesByScale.loyaltyMonth}>
					<Image
						source={VipActiveMemberLoyaltyBadge}
						style={stylesByScale.loyaltyBadge}
						resizeMode='contain'
					/>
					<Text allowFontScaling={false} style={stylesByScale.loyaltyNumber}>
						4
					</Text>
				</View>
				<View style={stylesByScale.loyaltyCopy}>
					<View style={stylesByScale.loyaltyHeader}>
						<Text
							allowFontScaling={false}
							style={[stylesByScale.captionGold, stylesByScale.loyaltyMonthLabel]}
						>
							{t('T_VIP_MONTH', { month: 4 })}
						</Text>
						<Text allowFontScaling={false} style={stylesByScale.loyaltyStreak}>
							{t('T_VIP_KEEP_STREAK')}
						</Text>
						<Text
							allowFontScaling={false}
							style={[stylesByScale.captionGold, stylesByScale.loyaltyCount]}
						>
							4 / 12
						</Text>
					</View>
					<View style={stylesByScale.progressTrack}>
						<View style={stylesByScale.progressValue} />
					</View>
					<Text allowFontScaling={false} style={stylesByScale.label}>
						{t('T_VIP_NEXT_LOYALTY_REWARD', { month: 6 })}
					</Text>
				</View>
				<Image
					source={VipActiveMemberLoyaltyReward}
					style={stylesByScale.loyaltyReward}
					resizeMode='contain'
				/>
			</VipPanel>

			<View style={stylesByScale.rewardsRow}>
				<VipPanel scale={scale} style={[stylesByScale.rewardCard, stylesByScale.monthlyRewardCard]}>
					<View style={stylesByScale.rewardTopRow}>
						<Image
							source={VipActiveMemberWeeklyGift}
							style={stylesByScale.weeklyGift}
							resizeMode='contain'
						/>
						<View style={stylesByScale.rewardTextColumn}>
							<Text
								adjustsFontSizeToFit
								allowFontScaling={false}
								minimumFontScale={0.8}
								numberOfLines={1}
								style={stylesByScale.weeklyRewardTitle}
							>
								{t('T_VIP_WEEKLY_REWARD')}
							</Text>
							<Text style={stylesByScale.label}>{t('T_VIP_READY_TO_CLAIM')}</Text>
							<Text style={stylesByScale.captionGold}>2d 14h 22m</Text>
						</View>
					</View>
					<View style={stylesByScale.claimButton}>
						<Text style={stylesByScale.claimText}>{t('T_VIP_CLAIM_REWARD')}</Text>
					</View>
				</VipPanel>
				<VipPanel scale={scale} style={stylesByScale.rewardCard}>
					<View style={stylesByScale.monthlyRewardTopRow}>
						<Image
							source={VipActiveMemberCalendar}
							style={stylesByScale.calendar}
							resizeMode='contain'
						/>
						<View style={stylesByScale.rewardTextColumn}>
							<Text style={stylesByScale.title}>{t('T_VIP_MONTHLY_RENEWAL_REWARD')}</Text>
							<Text style={stylesByScale.label}>{t('T_VIP_NEXT_REWARD_ON')}</Text>
							<Text style={stylesByScale.captionGold}>12 AUG 2025</Text>
						</View>
					</View>
					<Text numberOfLines={1} style={stylesByScale.rewardNote}>
						{t('T_VIP_AFTER_RENEWAL')}
					</Text>
				</VipPanel>
			</View>
		</>
	);

	const includedBlocks = (stylesByScale: ScaledStyles, scale: number) => (
		<>
			<VipPanel scale={scale} variant='section' style={stylesByScale.sectionCard}>
				<SectionHeading
					title={t('T_VIP_EVENT_PASSES')}
					accessory={`${t('T_VIP_VIEW_ALL')} ›`}
					scale={scale}
				/>
				<EventPass title={t('T_VIP_EVENT_SUMMER_CLASH')} time='6d 14h' scale={scale} />
				<EventPass title={t('T_VIP_EVENT_POKER_MASTERS')} time='13d 14h' scale={scale} />
			</VipPanel>

			<VipPanel scale={scale} variant='section' style={stylesByScale.cosmeticsCard}>
				<SectionHeading
					title={t('T_VIP_COSMETICS')}
					accessory={t('T_VIP_WHILE_SUBSCRIBED')}
					scale={scale}
				/>
				<View style={stylesByScale.cosmeticsRow}>
					<VipPanel scale={scale} style={stylesByScale.cosmeticCard}>
						<Image
							source={VipActiveMemberProfileFrame}
							style={stylesByScale.cosmeticArt}
							resizeMode='contain'
						/>
						<Text style={stylesByScale.cosmeticTitle}>{t('T_VIP_PROFILE_FRAME')}</Text>
					</VipPanel>
					<VipPanel scale={scale} style={stylesByScale.cosmeticCard}>
						<Image
							source={VipActiveMemberCardBack}
							style={stylesByScale.cosmeticArt}
							resizeMode='contain'
						/>
						<Text style={stylesByScale.cosmeticTitle}>{t('T_VIP_CARD_BACK')}</Text>
					</VipPanel>
				</View>
			</VipPanel>

			<VipPanel scale={scale} variant='highlight' style={stylesByScale.manageRow}>
				<Image
					source={VipActiveMemberSubscriptionSettings}
					style={stylesByScale.manageIcon}
					resizeMode='contain'
				/>
				<View style={stylesByScale.manageCopy}>
					<Text style={stylesByScale.title}>{t('T_VIP_MANAGE_SUBSCRIPTION')}</Text>
					<Text style={stylesByScale.label}>{t('T_VIP_MANAGE_SUBSCRIPTION_DESC')}</Text>
				</View>
				<Text style={stylesByScale.chevron}>›</Text>
			</VipPanel>
		</>
	);

	if (desktop.isDesktopLayout) {
		// The desktop frame in the desktop home's two columns, each block set sized to its column
		// the way the mobile screen sizes them to its 360-wide one.
		const columns = getDesktopColumns(desktop.contentWidth, desktop.scale);
		const leftScale = columns.left / DESIGN_CONTENT_WIDTH;
		const rightScale = columns.right / DESIGN_CONTENT_WIDTH;
		const leftStyles = getScaledStyles(leftScale, columns.left, 0);
		const rightStyles = getScaledStyles(rightScale, columns.right, 0);

		return (
			<MainLayout>
				<IndicatorScrollView
					style={[styles.scroll, desktopScrollFade(desktop.s(40))]}
					reserveGutter={false}
					contentContainerStyle={[
						styles.desktopContent,
						{
							paddingHorizontal: desktop.side,
							paddingTop: desktop.s(8),
							paddingBottom: desktop.s(40),
							columnGap: columns.gap,
						},
					]}
					showsVerticalScrollIndicator={false}
				>
					<View style={{ width: columns.left, rowGap: 14 * leftScale }}>
						{membershipBlocks(leftStyles, leftScale)}
					</View>
					<View style={{ width: columns.right, rowGap: 14 * rightScale }}>
						{includedBlocks(rightStyles, rightScale)}
					</View>
				</IndicatorScrollView>
			</MainLayout>
		);
	}

	return (
		<MainLayout>
			<IndicatorScrollView
				style={styles.scroll}
				reserveGutter={false}
				contentContainerStyle={stylesByScale.scrollContent}
				showsVerticalScrollIndicator={false}
			>
				{membershipBlocks(stylesByScale, scale)}
				{includedBlocks(stylesByScale, scale)}

				<View style={stylesByScale.footerSpacer} />
			</IndicatorScrollView>
		</MainLayout>
	);
};

type ScaledStyles = ReturnType<typeof getScaledStyles>;

const getScaledStyles = (scale: number, contentWidth: number, bottomInset: number) =>
	StyleSheet.create({
		scrollContent: {
			alignItems: 'center',
			paddingTop: 8 * scale,
			paddingBottom: 19 * scale,
			gap: 14 * scale,
		},
		membershipHero: {
			width: contentWidth,
			height: 140 * scale,
			flexDirection: 'row',
			alignItems: 'center',
		},
		membershipBanner: {
			...StyleSheet.absoluteFillObject,
		},
		membershipStatus: {
			flex: 1,
			padding: 12 * scale,
			paddingLeft: 147 * scale,
			alignItems: 'flex-end',
		},
		membershipTitle: {
			textAlign: 'right',
		},
		title: {
			color: '#ffff99',
			fontFamily: FONT_FAMILY_DISPLAY,
			fontSize: 16 * scale,
			lineHeight: 19 * scale,
			fontWeight: '600',
		},
		activeRow: {
			marginTop: 8 * scale,
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'flex-end',
		},
		activeText: {
			color: '#0ceb66',
			fontFamily: FONT_FAMILY_DISPLAY,
			fontSize: 28 * scale,
			lineHeight: 28 * scale,
			fontWeight: '900',
			letterSpacing: 0.8 * scale,
		},
		billingBlock: {
			marginTop: 8 * scale,
			flexDirection: 'row',
			alignItems: 'baseline',
			justifyContent: 'flex-end',
			gap: 4 * scale,
		},
		billingLabel: {
			color: '#ffff99',
			fontFamily: FONT_FAMILY,
			fontSize: 10 * scale,
			lineHeight: 14 * scale,
			fontWeight: '600',
		},
		billingDate: {
			color: '#ebe3d6',
			fontFamily: FONT_FAMILY_DISPLAY,
			fontSize: 16 * scale,
			lineHeight: 18 * scale,
			fontWeight: '900',
			letterSpacing: 0.64 * scale,
		},
		renewalNotice: {
			marginTop: 6 * scale,
			textAlign: 'right',
			color: '#ffff99',
			fontFamily: FONT_FAMILY,
			fontSize: 10 * scale,
			lineHeight: 10 * scale,
			fontWeight: '600',
		},
		loyaltyCard: {
			width: contentWidth,
			minHeight: 85 * scale,
			paddingLeft: 12 * scale,
			paddingTop: 12 * scale,
			paddingBottom: 12 * scale,
			flexDirection: 'row',
			alignItems: 'center',
			gap: 12 * scale,
		},
		loyaltyMonth: {
			width: 48 * scale,
			height: 62 * scale,
			alignItems: 'center',
			justifyContent: 'center',
		},
		loyaltyBadge: {
			width: 70 * scale,
			height: 64 * scale,
		},
		loyaltyNumber: {
			position: 'absolute',
			left: 0,
			top: 14 * scale,
			width: 48 * scale,
			color: '#ffff99',
			fontFamily: FONT_FAMILY,
			fontSize: 22 * scale,
			lineHeight: 29 * scale,
			fontWeight: '700',
			textAlign: 'center',
		},
		loyaltyCopy: {
			width: contentWidth - 136 * scale,
			gap: 5 * scale,
		},
		loyaltyHeader: {
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'space-between',
		},
		captionGold: {
			color: '#ffff99',
			fontFamily: FONT_FAMILY,
			fontSize: 12 * scale,
			lineHeight: 17 * scale,
			fontWeight: '700',
			letterSpacing: 0.12 * scale,
		},
		loyaltyMonthLabel: {
			width: 50 * scale,
		},
		loyaltyStreak: {
			color: '#fff',
			fontFamily: FONT_FAMILY,
			fontSize: 12 * scale,
			lineHeight: 14 * scale,
			fontWeight: '600',
			width: 121 * scale,
		},
		loyaltyCount: {
			width: 30 * scale,
			textAlign: 'right',
		},
		progressTrack: {
			height: 10 * scale,
			borderRadius: 5 * scale,
			borderWidth: 1,
			borderColor: '#da600e',
			backgroundColor: '#160503',
			overflow: 'hidden',
		},
		progressValue: {
			width: '33.3%',
			height: '100%',
			borderRadius: 5 * scale,
			backgroundColor: '#e19010',
		},
		label: {
			color: '#fff',
			fontFamily: FONT_FAMILY,
			fontSize: 12 * scale,
			lineHeight: 14 * scale,
			fontWeight: '600',
		},
		loyaltyReward: {
			position: 'absolute',
			right: 3 * scale,
			top: 15 * scale,
			width: 61 * scale,
			height: 53 * scale,
		},
		rewardsRow: {
			width: contentWidth,
			flexDirection: 'row',
			gap: 12 * scale,
		},
		rewardCard: {
			flex: 1,
			minHeight: 127 * scale,
			paddingTop: 12 * scale,
			paddingHorizontal: 12 * scale,
			paddingBottom: 15 * scale,
			justifyContent: 'space-between',
		},
		rewardTopRow: {
			height: 59 * scale,
			flexDirection: 'row',
			alignItems: 'flex-start',
			gap: 8 * scale,
		},
		monthlyRewardCard: {
			justifyContent: 'flex-start',
		},
		monthlyRewardTopRow: {
			height: 72 * scale,
			flexDirection: 'row',
			alignItems: 'flex-start',
			gap: 8 * scale,
		},
		weeklyGift: {
			width: 40 * scale,
			height: 50 * scale,
			marginLeft: -3 * scale,
			marginTop: -7 * scale,
		},
		calendar: {
			width: 42 * scale,
			height: 40 * scale,
			marginLeft: -6 * scale,
		},
		rewardTextColumn: {
			flex: 1,
			gap: 3 * scale,
		},
		weeklyRewardTitle: {
			width: 100 * scale,
			color: '#ffff99',
			fontFamily: FONT_FAMILY_DISPLAY,
			fontSize: 16 * scale,
			lineHeight: 19 * scale,
			fontWeight: '600',
		},
		claimButton: {
			height: 30 * scale,
			borderRadius: 10 * scale,
			borderWidth: 1,
			borderColor: 'rgba(168,255,133,0.7)',
			backgroundColor: '#1c801a',
			alignItems: 'center',
			justifyContent: 'center',
		},
		claimText: {
			color: '#fff',
			fontFamily: FONT_FAMILY,
			fontSize: 14 * scale,
			fontWeight: '700',
		},
		rewardNote: {
			marginTop: 8 * scale,
			color: '#fff',
			fontFamily: FONT_FAMILY,
			fontSize: 10 * scale,
			lineHeight: 12 * scale,
			fontWeight: '500',
		},
		sectionCard: {
			width: contentWidth,
			padding: 12 * scale,
			gap: 8 * scale,
		},
		cosmeticsCard: {
			width: contentWidth,
			padding: 12 * scale,
			gap: 8 * scale,
		},
		cosmeticsRow: {
			flexDirection: 'row',
			gap: 12 * scale,
		},
		cosmeticCard: {
			flex: 1,
			minHeight: 143 * scale,
			alignItems: 'center',
			paddingTop: 12 * scale,
		},
		cosmeticArt: {
			width: 96 * scale,
			height: 96 * scale,
		},
		cosmeticTitle: {
			color: '#ffff99',
			fontFamily: FONT_FAMILY_DISPLAY,
			fontSize: 16 * scale,
			fontWeight: '600',
			lineHeight: 19 * scale,
			marginTop: 7 * scale,
			textAlign: 'center',
			width: '100%',
		},
		manageRow: {
			width: contentWidth,
			minHeight: 70 * scale,
			padding: 12 * scale,
			flexDirection: 'row',
			alignItems: 'center',
			gap: 12 * scale,
		},
		manageIcon: {
			width: 32 * scale,
			height: 32 * scale,
		},
		manageCopy: {
			flex: 1,
			gap: 3 * scale,
		},
		chevron: {
			color: '#ffff99',
			fontFamily: FONT_FAMILY,
			fontSize: 22 * scale,
			lineHeight: 29 * scale,
			fontWeight: '700',
		},
		footerSpacer: {
			width: contentWidth,
			height: 8 * scale + bottomInset,
		},
	});

const styles = StyleSheet.create({
	scroll: {
		flex: 1,
		minHeight: 0,
	},
	desktopContent: {
		flexDirection: 'row',
		alignItems: 'flex-start',
	},
	surfaceField: {
		...StyleSheet.absoluteFillObject,
	},
	panel: {
		borderWidth: 1,
		borderColor: '#fcf17b',
		overflow: 'hidden',
		...vipCardGlow,
	},
	sectionHeading: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	title: {
		color: '#ffff99',
		fontFamily: FONT_FAMILY_DISPLAY,
		fontWeight: '600',
	},
	headingAccessory: {
		color: '#ffff99',
		fontFamily: FONT_FAMILY,
		fontWeight: '500',
	},
	eventPass: {
		flexDirection: 'row',
		alignItems: 'center',
		borderColor: '#da600e',
	},
	eventThumb: {
		backgroundColor: '#ab1b08',
		borderWidth: 1,
		borderColor: 'rgba(255,245,185,0.45)',
	},
	eventCopy: {
		flex: 1,
	},
	label: {
		color: '#fff',
		fontFamily: FONT_FAMILY,
		fontWeight: '500',
	},
	muted: {
		color: '#ebe3d6',
		fontFamily: FONT_FAMILY,
		fontWeight: '500',
	},
	rotateRoot: {
		flex: 1,
		backgroundColor: '#080100',
		alignItems: 'center',
		justifyContent: 'center',
		padding: 24,
	},
	rotateText: {
		color: '#ffff99',
		fontFamily: FONT_FAMILY,
		fontSize: 16,
		fontWeight: '600',
		textAlign: 'center',
	},
});

export default VipActiveMemberScreen;
